import {
	loadBeacon4PerformanceDashboard,
	loadBeacon4PerformanceTable,
	loadBeacon4PerformanceAvailableKpis,
	clearBeacon4PerformanceDashboard,
} from '../reducers/Beacon4PerformanceKpiDataReducer';
import {
	resetBeacon4HiddenSeries,
	setBeacon4PerfTableKpi,
	setBeacon4PerfAggregationMode,
	setBeacon4PerfMainKpi,
	setBeacon4AdminDialog,
} from '../reducers/Beacon4FiltersReducer';

// Builds the API payload. The two `*Override` knobs let us reuse this for the
// table-only reload, where either the KPI or the aggregation mode may differ
// from what the dashboard last used.
const buildParams = (filters, { kpiOverride, aggModeOverride } = {}) => {
	const p = {
		tech:            filters.perfTech,
		mainKpiKey:      kpiOverride     ?? filters.perfMainKpi,
		dateFrom:        filters.dateFrom,
		dateTo:          filters.dateTo,
		aggregation:     filters.aggregation,
		aggregationMode: aggModeOverride ?? filters.perfAggregationMode,
	};
	if (filters.side)    p.side    = filters.side;
	if (filters.polygon) p.polygon = filters.polygon;
	if (filters.jvid)    p.jvId    = filters.jvid;
	return p;
};

export const loadBeacon4PerformanceView = () => async (dispatch, getState) => {
	const filters = getState().beacon4Filters;
	if (!filters.perfTech || !filters.perfMainKpi) return;
	const params = buildParams(filters);
	dispatch(resetBeacon4HiddenSeries());
	// Apply resets the table's KPI back to the dashboard's Main KPI so the
	// two panels stay aligned right after the user presses Apply. Afterwards,
	// the user is free to pick another KPI in the table without re-running Apply.
	dispatch(setBeacon4PerfTableKpi(filters.perfMainKpi));
	// Charts + table loaded in parallel
	await Promise.all([
		dispatch(loadBeacon4PerformanceDashboard(params)),
		dispatch(loadBeacon4PerformanceTable(params)),
	]);
};

export const reloadBeacon4PerformanceView = () => (dispatch) => {
	dispatch(loadBeacon4PerformanceView());
};

// Reloads ONLY the aggregation table for a different KPI and/or aggregation
// mode (used by the table's own controls). The charts are untouched. Pass
// `{ kpiKey }` to change the KPI or `{ aggMode }` to change the grouping —
// either or both. The Redux state for whatever you pass is updated too, so the
// new selection persists across subsequent Apply presses (until Apply resets
// kpi back to the dashboard's main KPI, by design).
export const reloadBeacon4PerformanceTableOnly = ({ kpiKey, aggMode } = {}) => async (dispatch, getState) => {
	const filters = getState().beacon4Filters;
	if (!filters.perfTech) return;
	if (kpiKey)  dispatch(setBeacon4PerfTableKpi(kpiKey));
	if (aggMode) dispatch(setBeacon4PerfAggregationMode(aggMode));
	// When only the Aggregation Mode changes (kpiKey is undefined here),
	// the KPI override must still fall back to the table's OWN current
	// selection (perfTableKpi) — NOT straight to filters.perfMainKpi like
	// buildParams' default does for every other caller. Without this, picking
	// a sub-KPI in the table dropdown and then switching Aggregation Mode
	// silently re-fetches the dashboard's Main KPI instead of the sub-KPI
	// still showing in the dropdown, because buildParams() has no idea a
	// table-specific KPI is even in play unless it's passed in explicitly.
	const params = buildParams(filters, {
		kpiOverride: kpiKey ?? filters.perfTableKpi,
		aggModeOverride: aggMode,
	});
	// Need at least a KPI to fetch — bail silently otherwise.
	if (!params.mainKpiKey) return;
	await dispatch(loadBeacon4PerformanceTable(params));
};

export const clearBeacon4PerformanceData = () => (dispatch) => {
	dispatch(clearBeacon4PerformanceDashboard());
};

// Closing "Manage KPIs" used to leave the dashboard stale — KPIs can be
// created, renamed, deleted, or have their formula/breakdowns/SLA thresholds
// edited inside that dialog, but none of that invalidates the already-cached
// dashboard/table data (keyed by tech+KPI, see Beacon4PerformanceKpiDataReducer)
// or the Tech/KPI dropdown lookup (fetched once when the module first opened).
// The only fix used to be a full browser refresh. This replicates that
// refresh in-app instead:
//   1. Drop every cached dashboard/table entry — not just the combo on
//      screen, since the user may have applied several tech/KPI combos
//      earlier in the session and any of them could be stale now.
//   2. Re-fetch the Tech/KPI dropdown lookup.
//   3. If the KPI currently selected no longer exists (deleted, or renamed
//      to a different key), clear the selection instead of re-querying a
//      KPI the backend no longer knows about.
//   4. Otherwise, re-run Apply automatically so the user sees fresh data
//      immediately, without having to remember to press Apply themselves.
export const closeBeacon4PerformanceAdmin = () => async (dispatch, getState) => {
	dispatch(setBeacon4AdminDialog(false));
	dispatch(clearBeacon4PerformanceDashboard());

	let available = null;
	try {
		available = await dispatch(loadBeacon4PerformanceAvailableKpis()).unwrap();
	} catch (e) {
		// Dropdown refresh failed — fall through and still try to reload the
		// current selection below; Apply remains available as a manual retry.
	}

	const filters = getState().beacon4Filters;
	if (available && filters.perfMainKpi) {
		const stillExists = (available.kpisByTech?.[filters.perfTech] ?? [])
			.some((k) => k.key === filters.perfMainKpi);
		if (!stillExists) {
			dispatch(setBeacon4PerfMainKpi(''));
			return;
		}
	}

	if (filters.perfTech && filters.perfMainKpi) {
		dispatch(loadBeacon4PerformanceView());
	}
};